import { useUser } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';

import i18n from '@/shared/i18n';

interface ContactFormData {
  email: string;
  message: string;
}

interface DiscordWebhookPayload {
  content: string;
}

export const useSendContactForm = () => {
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({ email, message }: ContactFormData) => {
      const webhookUrl = import.meta.env.VITE_DISCORD_CONTACT_WEBHOOK;

      if (!webhookUrl) {
        throw new Error('Discord webhook URL is not configured');
      }

      const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
      const now = new Date();
      const formattedDate = now.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const formattedTime = now.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      // Récupérer l'email principal de l'utilisateur Clerk
      const userEmail = user?.emailAddresses?.[0]?.emailAddress || 'Non disponible';
      const userId = user?.id || 'Non disponible';

      const payload: DiscordWebhookPayload = {
        content:
          `📬 **Nouvelle demande de contact**\n\n` +
          `📅 **Date:** ${formattedDate} à ${formattedTime}\n` +
          `🆔 **ID Utilisateur:** ${userId}\n` +
          `📧 **Email de connexion:** ${userEmail}\n` +
          `✉️ **Email de contact:** ${email}\n` +
          `💬 **Message:**\n\`\`\`\n${message}\n\`\`\``,
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to send contact form');
      }

      return { success: true };
    },
  });
};
